import { useEffect, useMemo, useRef, useState } from "react";
import { api, currency, qty as fmtQty } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, Receipt, ShoppingCart, Tag, Sparkles, Percent, X, CheckCircle2, Clock, User, Phone, Coins } from "lucide-react";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import RegisterCustomerDialog from "@/components/RegisterCustomerDialog";

function parseDeal(deal) {
  if (!deal) return null;
  const m = String(deal).toLowerCase().match(/(\d+)\s*for\s*r?\s*([\d.]+)/);
  if (!m) return null;
  return { n: parseInt(m[1], 10), p: parseFloat(m[2]) };
}
function lineTotal(qtyN, price, deal) {
  const d = parseDeal(deal);
  if (!d) return qtyN * price;
  const bundles = Math.floor(qtyN / d.n);
  const remainder = qtyN - bundles * d.n;
  return bundles * d.p + remainder * price;
}

function ruleString(d) {
  if (d.deal_price && d.required_quantity) return `Any ${d.required_quantity} eligible for ${currency(d.deal_price)}`;
  if (d.discount_percentage) return `${d.discount_percentage}% off eligible items`;
  if (d.discount_amount) return `${currency(d.discount_amount)} off eligible items`;
  if (d.buy_quantity && d.get_quantity) return `Buy ${d.buy_quantity}, get ${d.get_quantity} free`;
  return d.description || "";
}

function CartPanel({ inSheet, cart, subtotal, dealPreview, payment, setPayment, saleNote, setSaleNote, splitCash, setSplitCash, splitCard, setSplitCard, manualDiscount, discountRequest, openDiscount, clearDiscount, canApproveDirect, changeQty, removeLine, conclude, busy,
  customer, customerBalance, customerLoyaltyValue, phoneInput, setPhoneInput, searchingCustomer, findCustomer, detachCustomer, openRegister,
  redeemPoints, setRedeemPoints, loyaltyCfg, storeName }) {
  const discount = dealPreview?.discount_amount || 0;
  const afterDeal = dealPreview?.final_total ?? subtotal;
  // Compute manual discount monetary amount client-side for preview.
  let mdAmount = 0;
  if (manualDiscount) {
    const v = parseFloat(manualDiscount.value) || 0;
    mdAmount = manualDiscount.type === "percent"
      ? Math.round(afterDeal * (v / 100) * 100) / 100
      : Math.round(v * 100) / 100;
    if (mdAmount > afterDeal) mdAmount = afterDeal;
  }
  const finalTotalBeforeLoyalty = Math.max(0, Math.round((afterDeal - mdAmount) * 100) / 100);
  const valuePerPt = Number(loyaltyCfg?.rand_value_per_point || 1);
  const redeemN = Math.max(0, parseInt(redeemPoints || 0, 10) || 0);
  const maxRedeemByBalance = Math.max(0, Math.min(customerBalance || 0, Math.floor(finalTotalBeforeLoyalty / valuePerPt)));
  const clampedRedeem = Math.min(redeemN, maxRedeemByBalance);
  const redeemValue = Math.round(clampedRedeem * valuePerPt * 100) / 100;
  const finalTotal = Math.max(0, Math.round((finalTotalBeforeLoyalty - redeemValue) * 100) / 100);
  const applied = dealPreview?.applied;
  const isSplit = payment === "Split";
  const cashN = parseFloat(splitCash) || 0;
  const cardN = parseFloat(splitCard) || 0;
  const remaining = Math.round((finalTotal - cashN - cardN) * 100) / 100;
  const splitBalanced = isSplit ? Math.abs(remaining) < 0.005 && cashN >= 0 && cardN >= 0 : true;
  const setSplitOne = (which, raw) => {
    const val = raw === "" ? "" : String(Math.max(0, parseFloat(raw) || 0));
    if (which === "cash") {
      setSplitCash(val);
      const other = Math.max(0, Math.round((finalTotal - (parseFloat(val) || 0)) * 100) / 100);
      setSplitCard(other.toFixed(2));
    } else {
      setSplitCard(val);
      const other = Math.max(0, Math.round((finalTotal - (parseFloat(val) || 0)) * 100) / 100);
      setSplitCash(other.toFixed(2));
    }
  };
  return (
    <div className="flex flex-col bg-white h-full min-h-0">
      <div className="p-4 md:p-5 border-b border-border">
        <h2 className="font-display text-xl md:text-2xl">Current Sale</h2>
        {/* Customer panel — Membership */}
        <div className="mt-3 rounded-lg border border-border p-2.5 space-y-2" data-testid={inSheet ? "customer-panel-m" : "customer-panel"}>
          {customer ? (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <User className="w-3 h-3" strokeWidth={1.5} /> Member
                </div>
                <div className="text-sm font-medium truncate" data-testid={inSheet ? "attached-customer-name-m" : "attached-customer-name"}>
                  {customer.first_name} {customer.surname}
                </div>
                <div className="text-[11px] text-muted-foreground font-mono">{customer.phone_normalised || customer.phone_original}</div>
                <div className="text-[11px] text-primary font-mono" data-testid={inSheet ? "attached-customer-balance-m" : "attached-customer-balance"}>
                  {customerBalance} pts · {currency(customerLoyaltyValue)}
                </div>
              </div>
              <button type="button" onClick={detachCustomer} className="text-muted-foreground hover:text-[#C05746]" data-testid={inSheet ? "detach-customer-btn-m" : "detach-customer-btn"} title="Detach customer">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-1">
                <User className="w-3 h-3" strokeWidth={1.5} /> Customer <span className="normal-case tracking-normal text-[10px]">(optional)</span>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    className="pl-8 h-9 text-sm"
                    placeholder="Cellphone number"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") findCustomer(); }}
                    data-testid={inSheet ? "customer-phone-input-m" : "customer-phone-input"}
                  />
                </div>
                <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={findCustomer} disabled={searchingCustomer} data-testid={inSheet ? "customer-find-btn-m" : "customer-find-btn"}>
                  {searchingCustomer ? "…" : "Find"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto thin-scroll">
        {cart.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cart is empty. Tap a product to add.</div>
        ) : (
          <div className="divide-y">
            {cart.map(l => {
              const total = lineTotal(l.quantity, l.product.unit_price, l.product.special_deal);
              const tid = (base) => inSheet ? `m-${base}-${l.product.sku}` : `${base}-${l.product.sku}`;
              return (
                <div key={l.product.id} className="p-4" data-testid={tid("cart-line")}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{l.product.name}</div>
                      <div className="text-[11px] font-mono text-muted-foreground">
                        {currency(l.product.unit_price)} · {l.product.sku}
                      </div>
                    </div>
                    <button onClick={() => removeLine(l.product.id)} className="text-muted-foreground hover:text-red-600" data-testid={tid("remove")}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex items-center border rounded-full">
                      <button onClick={() => changeQty(l.product.id, -1)} className="p-2" data-testid={tid("dec")}>
                        <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <div className="w-10 text-center font-mono tabular-nums text-sm">{l.quantity}</div>
                      <button onClick={() => changeQty(l.product.id, +1)} className="p-2" data-testid={tid("inc")}>
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="font-mono font-semibold tabular-nums" data-testid={tid("line-total")}>{currency(total)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="p-4 md:p-5 border-t border-border space-y-3">
        {applied && (
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-3" data-testid={inSheet ? "applied-deal-m" : "applied-deal"}>
            <div className="flex items-center gap-2 text-primary">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm font-medium">{applied.deal_name}</span>
            </div>
            {applied.description && <div className="text-[11px] text-muted-foreground mt-1">{applied.description}</div>}
          </div>
        )}
        {/* Manual discount panel — Phase D */}
        {manualDiscount ? (
          <div className="rounded-lg bg-secondary/5 border border-secondary/30 p-3 space-y-1" data-testid={inSheet ? "manual-discount-panel-m" : "manual-discount-panel"}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-secondary">
                <Percent className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm font-medium">
                  Manual discount
                  {manualDiscount.type === "percent" ? ` ${manualDiscount.value}%` : ` R${Number(manualDiscount.value).toFixed(2)}`}
                </span>
              </div>
              <button
                type="button"
                onClick={clearDiscount}
                className="text-muted-foreground hover:text-[#C05746]"
                data-testid={inSheet ? "clear-discount-btn-m" : "clear-discount-btn"}
                title="Remove discount"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="text-[11px] text-muted-foreground">Reason: {manualDiscount.reason}</div>
            {manualDiscount.approved_by_name && (
              <div className="text-[11px] text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Approved by {manualDiscount.approved_by_name}
              </div>
            )}
          </div>
        ) : discountRequest ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-1" data-testid={inSheet ? "discount-request-pending-m" : "discount-request-pending"}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-700">
                {discountRequest.status === "pending" ? <Clock className="w-4 h-4" strokeWidth={1.5} /> : <X className="w-4 h-4" strokeWidth={1.5} />}
                <span className="text-sm font-medium">
                  Discount {discountRequest.status === "pending" ? "awaiting approval" : discountRequest.status}
                </span>
              </div>
              <button
                type="button"
                onClick={clearDiscount}
                className="text-muted-foreground hover:text-[#C05746]"
                data-testid={inSheet ? "clear-discount-req-btn-m" : "clear-discount-req-btn"}
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="text-[11px] text-muted-foreground">
              {discountRequest.discount_type === "percent" ? `${discountRequest.discount_value}%` : `R${Number(discountRequest.discount_value).toFixed(2)}`} · {discountRequest.reason}
            </div>
            {discountRequest.status === "rejected" && discountRequest.rejection_reason && (
              <div className="text-[11px] text-[#C05746]">Rejected: {discountRequest.rejection_reason}</div>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openDiscount}
            className="w-full rounded-full"
            data-testid={inSheet ? "open-discount-btn-m" : "open-discount-btn"}
          >
            <Percent className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            Add discount
          </Button>
        )}
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Payment</div>
          <Select value={payment} onValueChange={setPayment}>
            <SelectTrigger className="w-40" data-testid={inSheet ? "pos-payment-m" : "pos-payment"}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Cash">Cash</SelectItem>
              <SelectItem value="Card">Card</SelectItem>
              <SelectItem value="EFT">EFT</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
              <SelectItem value="Split">Split (Cash + Card)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isSplit && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2" data-testid={inSheet ? "pos-split-panel-m" : "pos-split-panel"}>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Cash</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={splitCash}
                  onChange={(e) => setSplitOne("cash", e.target.value)}
                  className="font-mono text-right"
                  data-testid={inSheet ? "pos-split-cash-m" : "pos-split-cash"}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Card</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={splitCard}
                  onChange={(e) => setSplitOne("card", e.target.value)}
                  className="font-mono text-right"
                  data-testid={inSheet ? "pos-split-card-m" : "pos-split-card"}
                />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-muted-foreground">Remaining</span>
              <span
                className={splitBalanced ? "text-emerald-700" : "text-[#C05746]"}
                data-testid={inSheet ? "pos-split-remaining-m" : "pos-split-remaining"}
              >
                {currency(remaining)}
              </span>
            </div>
            {!splitBalanced && (
              <div className="text-[11px] text-[#C05746]" data-testid={inSheet ? "pos-split-error-m" : "pos-split-error"}>
                Cash + Card must equal {currency(finalTotal)} before you can conclude.
              </div>
            )}
          </div>
        )}
        <div className="space-y-1">
          <label htmlFor={inSheet ? "pos-sale-note-m" : "pos-sale-note"} className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Sale Note <span className="normal-case tracking-normal text-[10px]">(optional)</span>
          </label>
          <Textarea
            id={inSheet ? "pos-sale-note-m" : "pos-sale-note"}
            data-testid={inSheet ? "pos-sale-note-m" : "pos-sale-note"}
            value={saleNote}
            onChange={(e) => setSaleNote(e.target.value)}
            placeholder="e.g. Customer collecting remaining item tomorrow"
            rows={2}
            maxLength={500}
            className="text-xs resize-none"
          />
        </div>
        {discount > 0 && (
          <div className="text-xs font-mono flex justify-between text-muted-foreground">
            <span>Original</span>
            <span data-testid={inSheet ? "pos-original-m" : "pos-original"}>{currency(subtotal)}</span>
          </div>
        )}
        {discount > 0 && (
          <div className="text-xs font-mono flex justify-between text-secondary">
            <span>Deal discount</span>
            <span data-testid={inSheet ? "pos-discount-m" : "pos-discount"}>− {currency(discount)}</span>
          </div>
        )}
        {mdAmount > 0 && (
          <div className="text-xs font-mono flex justify-between text-secondary">
            <span>Manual discount</span>
            <span data-testid={inSheet ? "pos-manual-discount-m" : "pos-manual-discount"}>− {currency(mdAmount)}</span>
          </div>
        )}
        {/* Loyalty redemption — Membership */}
        {customer && customerBalance > 0 && finalTotalBeforeLoyalty > 0 && (
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 space-y-2" data-testid={inSheet ? "loyalty-redeem-panel-m" : "loyalty-redeem-panel"}>
            <div className="flex items-center justify-between text-primary text-xs">
              <div className="flex items-center gap-1.5">
                <Coins className="w-4 h-4" strokeWidth={1.5} />
                <span>Redeem loyalty points</span>
              </div>
              <span className="font-mono">{customerBalance} pts available</span>
            </div>
            <div className="flex gap-2 items-center">
              <Input
                type="number" min="0" step="1" inputMode="numeric"
                value={redeemPoints}
                onChange={(e) => setRedeemPoints(e.target.value)}
                placeholder="0"
                className="font-mono text-right h-9"
                data-testid={inSheet ? "loyalty-redeem-input-m" : "loyalty-redeem-input"}
              />
              <Button
                type="button" size="sm" variant="outline" className="rounded-full text-[11px]"
                onClick={() => setRedeemPoints(String(maxRedeemByBalance))}
                data-testid={inSheet ? "loyalty-redeem-max-m" : "loyalty-redeem-max"}
              >Max</Button>
            </div>
            {clampedRedeem > 0 && (
              <div className="text-[11px] text-muted-foreground font-mono flex justify-between">
                <span>Redemption</span>
                <span className="text-primary">− {currency(redeemValue)} ({clampedRedeem} pts)</span>
              </div>
            )}
          </div>
        )}
        <div className="flex items-baseline justify-between">
          <div className="text-sm text-muted-foreground">Total</div>
          <div className="font-display text-3xl tabular-nums" data-testid={inSheet ? "pos-total-m" : "pos-total"}>{currency(finalTotal)}</div>
        </div>
        <Button
          data-testid={inSheet ? "conclude-sale-btn-m" : "conclude-sale-btn"}
          disabled={busy || cart.length === 0 || !splitBalanced || (!!discountRequest && discountRequest.status !== "approved")}
          onClick={conclude}
          className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-base"
        >
          <Receipt className="w-4 h-4 mr-2" strokeWidth={1.5} /> Conclude sale
        </Button>
        {storeName && (
          <div className="text-center text-xs text-muted-foreground" data-testid={inSheet ? "pos-checkout-store-m" : "pos-checkout-store"}>
            Selling from <span className="font-semibold text-foreground">{storeName}</span> — stock deducts from this store
          </div>
        )}
      </div>
    </div>
  );
}

export default function POS() {
  const { user } = useAuth();
  const { active: activeStore, setCartCount } = useStore();
  const canApproveDirect = ["platform_owner", "vendor_owner", "admin", "manager"].includes(user?.role);
  const CASHIER_PERCENT_THRESHOLD = 60;
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [meta, setMeta] = useState({ categories: [] });
  const [cart, setCart] = useState([]);
  useEffect(() => { setCartCount(cart.length); }, [cart.length, setCartCount]);
  useEffect(() => () => setCartCount(0), [setCartCount]);
  const [payment, setPayment] = useState("Cash");
  const [saleNote, setSaleNote] = useState("");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [dealsOpen, setDealsOpen] = useState(false);
  const [activeDeals, setActiveDeals] = useState([]);
  const [dealPreview, setDealPreview] = useState(null);
  const previewTimer = useRef(null);
  // Manual discount (Phase D)
  const [discountOpen, setDiscountOpen] = useState(false);
  const [dcType, setDcType] = useState("percent");
  const [dcValue, setDcValue] = useState("");
  const [dcReason, setDcReason] = useState("");
  const [manualDiscount, setManualDiscount] = useState(null); // {type, value, reason, [approved_by_name]}
  const [discountRequest, setDiscountRequest] = useState(null); // {id, status, ...}
  const pollTimer = useRef(null);

  // Membership / Loyalty state
  const [customer, setCustomer] = useState(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [prefillPhone, setPrefillPhone] = useState("");
  const [customerBalance, setCustomerBalance] = useState(0);
  const [redeemPoints, setRedeemPoints] = useState("");
  const [loyaltyCfg, setLoyaltyCfg] = useState({ rand_spend_per_point: 10, rand_value_per_point: 1 });
  const customerLoyaltyValue = customerBalance * (Number(loyaltyCfg?.rand_value_per_point) || 1);

  const findCustomer = async () => {
    const raw = phoneInput.trim();
    if (!raw) return toast.error("Enter a cellphone number");
    setSearchingCustomer(true);
    try {
      const r = await api.get("/customers", { params: { phone: raw } });
      const match = (r.data || [])[0];
      if (match) {
        setCustomer(match);
        setCustomerBalance(match.loyalty_balance || 0);
        toast.success(`Attached ${match.first_name} ${match.surname}`);
      } else {
        setPrefillPhone(raw);
        setRegisterOpen(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setSearchingCustomer(false); }
  };
  const detachCustomer = () => {
    setCustomer(null); setCustomerBalance(0); setRedeemPoints(""); setPhoneInput("");
  };
  const onCustomerRegistered = (c) => {
    setRegisterOpen(false);
    setCustomer(c); setCustomerBalance(c.loyalty_balance || 0);
    toast.success(`Attached ${c.first_name} ${c.surname}`);
  };
  useEffect(() => { api.get("/loyalty/config").then(r => setLoyaltyCfg(r.data)).catch(() => {}); }, []);

  const load = () =>
    api.get("/products", { params: { q: q || undefined, category: cat === "All" ? undefined : cat } })
      .then(r => setProducts(r.data.filter(p => p.active)));
  const loadDeals = () => api.get("/deals", { params: { active_only: true } }).then(r => setActiveDeals(r.data));

  useEffect(() => { load(); }, [q, cat]);
  useEffect(() => { api.get("/products/meta").then(r => setMeta(r.data)); loadDeals(); }, []);

  // Live deal preview (debounced) whenever cart changes
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (cart.length === 0) { setDealPreview(null); return; }
    previewTimer.current = setTimeout(async () => {
      try {
        const r = await api.post("/deals/preview", {
          lines: cart.map(l => ({ product_id: l.product.id, quantity: l.quantity })),
        });
        setDealPreview(r.data);
      } catch { /* silent */ }
    }, 250);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [cart]);

  const eligibleProductIds = useMemo(() => {
    const s = new Set();
    activeDeals.forEach(d => (d.eligible_product_ids || []).forEach(id => s.add(id)));
    return s;
  }, [activeDeals]);
  const eligibleCategories = useMemo(() => {
    const s = new Set();
    activeDeals.forEach(d => (d.eligible_categories || []).forEach(c => s.add(c)));
    return s;
  }, [activeDeals]);

  const addToCart = (p) => {
    if (!(Number(p.unit_price) > 0)) return toast.error("Selling price not confirmed — not available for checkout");
    if (p.pending_count) {
      toast.warning("Stock count pending — sale will be recorded, but current stock balance is unconfirmed.");
    } else if (p.quantity_on_hand <= 0) {
      return toast.error("Out of stock");
    }
    setCart((c) => {
      const idx = c.findIndex(x => x.product.id === p.id);
      if (idx >= 0) {
        if (!p.pending_count && c[idx].quantity + 1 > p.quantity_on_hand) {
          toast.error("Not enough stock"); return c;
        }
        const nc = [...c]; nc[idx] = { ...nc[idx], quantity: nc[idx].quantity + 1 };
        return nc;
      }
      return [...c, { product: p, quantity: 1 }];
    });
  };

  const changeQty = (pid, delta) => {
    setCart((c) => c.map(item => {
      if (item.product.id !== pid) return item;
      const newQ = item.quantity + delta;
      if (newQ <= 0) return null;
      if (!item.product.pending_count && newQ > item.product.quantity_on_hand) {
        toast.error("Not enough stock"); return item;
      }
      return { ...item, quantity: newQ };
    }).filter(Boolean));
  };
  const removeLine = (pid) => setCart(c => c.filter(x => x.product.id !== pid));

  const subtotal = useMemo(
    () => cart.reduce((s, l) => s + lineTotal(l.quantity, l.product.unit_price, l.product.special_deal), 0),
    [cart]
  );
  const totalItems = cart.reduce((s, l) => s + l.quantity, 0);
  const displayFinalTotal = dealPreview?.final_total ?? subtotal;

  // Whenever the payment method changes to Split (or the cart/discount changes),
  // pre-fill both fields to a sensible default so the cashier just tweaks one side.
  useEffect(() => {
    if (payment !== "Split") { setSplitCash(""); setSplitCard(""); return; }
    const finalT = dealPreview?.final_total ?? subtotal;
    // Only auto-init when the fields are empty so we don't clobber cashier input.
    setSplitCash((prev) => (prev === "" ? "0.00" : prev));
    setSplitCard((prev) => (prev === "" ? Number(finalT || 0).toFixed(2) : prev));
  }, [payment, subtotal, dealPreview?.final_total]);

  const openDiscount = () => {
    setDcType("percent"); setDcValue(""); setDcReason("");
    setDiscountOpen(true);
  };
  const clearDiscount = () => {
    setManualDiscount(null);
    setDiscountRequest(null);
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
  };
  const submitDiscount = async () => {
    const v = parseFloat(dcValue);
    const reason = dcReason.trim();
    if (!v || v <= 0) return toast.error("Enter a positive value");
    if (dcType === "percent" && v > 100) return toast.error("Percentage cannot exceed 100");
    if (!reason) return toast.error("Reason is required");
    // Effective-percentage authority: works for both percent and fixed
    // discounts. Uses the same available total the backend uses (deal-applied
    // subtotal). Backend re-validates on POST /sales.
    const available = dealPreview?.final_total ?? subtotal;
    const clampedAmount = dcType === "percent"
      ? Math.min(available, Math.round(available * (v / 100) * 100) / 100)
      : Math.min(available, Math.round(v * 100) / 100);
    const effectivePct = available > 0
      ? Math.round((clampedAmount / available) * 10000) / 100
      : 0;
    const cashierSelfApply = !canApproveDirect
      && effectivePct <= CASHIER_PERCENT_THRESHOLD;
    if (canApproveDirect || cashierSelfApply) {
      setManualDiscount({
        type: dcType, value: v, reason,
        approved_by_name: user?.name,
      });
      setDiscountOpen(false);
      toast.success("Manual discount applied");
      return;
    }
    // Cashier route → server-side approval workflow.
    try {
      const r = await api.post("/discount-requests", {
        discount_type: dcType, discount_value: v, reason,
      });
      setDiscountRequest(r.data);
      setDiscountOpen(false);
      toast.info("Discount request submitted — awaiting approval");
      // Poll for approval.
      if (pollTimer.current) clearInterval(pollTimer.current);
      pollTimer.current = setInterval(async () => {
        try {
          const upd = await api.get(`/discount-requests/${r.data.id}`);
          setDiscountRequest(upd.data);
          if (upd.data.status === "approved") {
            clearInterval(pollTimer.current); pollTimer.current = null;
            setManualDiscount({
              type: upd.data.discount_type,
              value: upd.data.discount_value,
              reason: upd.data.reason,
              approved_by_name: upd.data.approved_by_name,
              request_id: upd.data.id,
            });
            toast.success(`Discount approved by ${upd.data.approved_by_name}`);
          } else if (upd.data.status === "rejected") {
            clearInterval(pollTimer.current); pollTimer.current = null;
            toast.error(`Discount rejected${upd.data.rejection_reason ? ": " + upd.data.rejection_reason : ""}`);
          }
        } catch { /* silent */ }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit request");
    }
  };

  useEffect(() => () => { if (pollTimer.current) clearInterval(pollTimer.current); }, []);

  const conclude = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    setBusy(true);
    try {
      const trimmedNote = saleNote.trim();
      const payload = {
        lines: cart.map(l => ({ product_id: l.product.id, quantity: l.quantity })),
        payment_method: payment,
        sale_note: trimmedNote || null,
      };
      // Attach manual discount info.
      if (manualDiscount) {
        if (manualDiscount.request_id) {
          payload.discount_request_id = manualDiscount.request_id;
        } else {
          payload.manual_discount_type = manualDiscount.type;
          payload.manual_discount_value = manualDiscount.value;
          payload.manual_discount_reason = manualDiscount.reason;
        }
      }
      // Split validation against local final total.
      const afterDeal = dealPreview?.final_total ?? subtotal;
      const mdAmount = manualDiscount
        ? (manualDiscount.type === "percent"
            ? Math.min(afterDeal, Math.round(afterDeal * (parseFloat(manualDiscount.value) / 100) * 100) / 100)
            : Math.min(afterDeal, Math.round(parseFloat(manualDiscount.value) * 100) / 100))
        : 0;
      const finalT = Math.max(0, Math.round((afterDeal - mdAmount) * 100) / 100);
      // Loyalty redemption reduces the amount the customer pays.
      const valuePerPt = Number(loyaltyCfg?.rand_value_per_point) || 1;
      const redeemN = customer ? Math.max(0, parseInt(redeemPoints || 0, 10) || 0) : 0;
      const maxRedeem = Math.max(0, Math.min(customerBalance || 0, Math.floor(finalT / valuePerPt)));
      const clampedRedeem = Math.min(redeemN, maxRedeem);
      if (clampedRedeem > 0) payload.loyalty_redeem_points = clampedRedeem;
      if (customer) payload.customer_id = customer.id;
      const redemptionValue = Math.round(clampedRedeem * valuePerPt * 100) / 100;
      const finalAfterLoyalty = Math.max(0, Math.round((finalT - redemptionValue) * 100) / 100);
      if (payment === "Split") {
        const cashN = Math.round((parseFloat(splitCash) || 0) * 100) / 100;
        const cardN = Math.round((parseFloat(splitCard) || 0) * 100) / 100;
        if (Math.abs(cashN + cardN - finalAfterLoyalty) > 0.005) {
          toast.error(`Split must equal ${currency(finalAfterLoyalty)}`);
          setBusy(false);
          return;
        }
        payload.payment_breakdown = { cash: cashN, card: cardN };
      }
      const r = await api.post("/sales", payload);
      setReceipt(r.data);
      setCart([]); setDealPreview(null); setSaleNote("");
      setSplitCash(""); setSplitCard(""); setPayment("Cash");
      clearDiscount();
      detachCustomer();
      setCartOpen(false);
      load();
      const earned = r.data.loyalty_points_earned || 0;
      const suffix = earned > 0 ? ` — +${earned} pts earned` : "";
      toast.success(r.data.deal_id ? `Sale completed — ${r.data.deal_name} applied${suffix}` : `Sale completed${suffix}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sale failed");
    } finally { setBusy(false); }
  };

  const cartProps = { cart, subtotal, dealPreview, payment, setPayment, saleNote, setSaleNote, splitCash, setSplitCash, splitCard, setSplitCard, manualDiscount, discountRequest, openDiscount, clearDiscount, canApproveDirect, changeQty, removeLine, conclude, busy,
    customer, customerBalance, customerLoyaltyValue, phoneInput, setPhoneInput, searchingCustomer, findCustomer, detachCustomer, openRegister: () => { setPrefillPhone(phoneInput); setRegisterOpen(true); },
    redeemPoints, setRedeemPoints, loyaltyCfg, storeName: activeStore?.store_name };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] h-[calc(100vh-52px)] md:h-screen">
      <div className="flex flex-col min-h-0 border-r border-border">
        <div className="p-4 md:p-5 border-b border-border bg-white">
          <div className="flex items-center gap-3 mb-3 md:mb-4">
            <h1 className="font-display text-2xl md:text-3xl" data-testid="pos-title">Register</h1>
            <Badge className="bg-primary/10 text-primary hidden md:inline-flex">{cart.length} in cart</Badge>
            <div className="ml-auto">
              <Sheet open={dealsOpen} onOpenChange={setDealsOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full" data-testid="current-deals-btn">
                    <Tag className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                    Current Deals
                    {activeDeals.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground h-5">{activeDeals.length}</Badge>}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
                  <SheetTitle className="font-display text-2xl">Current Deals</SheetTitle>
                  <div className="flex-1 overflow-y-auto thin-scroll -mx-6 px-6 pt-3" data-testid="current-deals-list">
                    {activeDeals.length === 0 ? (
                      <div className="text-sm text-muted-foreground text-center py-16">No active deals right now.</div>
                    ) : activeDeals.map(d => (
                      <div key={d.id} className="border rounded-lg p-3 mb-3" data-testid={`deals-sheet-${d.id}`}>
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                          <div className="font-medium text-sm">{d.name}</div>
                        </div>
                        <div className="text-xs mt-1 text-muted-foreground">{ruleString(d)}</div>
                        {(d.eligible_categories?.length || d.eligible_product_ids?.length) ? (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {(d.eligible_categories || []).map(c => (
                              <Badge key={c} className="bg-primary/10 text-primary hover:bg-primary/10 text-[10px]">{c}</Badge>
                            ))}
                            {(d.eligible_product_ids || []).slice(0, 6).map(pid => {
                              const p = products.find(x => x.id === pid);
                              return p ? <Badge key={pid} variant="secondary" className="text-[10px]">{p.sku}</Badge> : null;
                            })}
                          </div>
                        ) : null}
                        {d.end_at && (
                          <div className="text-[10px] text-muted-foreground mt-2 uppercase tracking-widest">Ends {new Date(d.end_at).toLocaleString("en-ZA")}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input data-testid="pos-search" className="pl-9" placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="md:w-56" data-testid="pos-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Categories</SelectItem>
                {meta.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll p-4 md:p-5 pb-24 lg:pb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {products.map(p => {
              const pending = !!p.pending_count;
              const noPrice = !(Number(p.unit_price) > 0);
              const out = noPrice || (!pending && p.quantity_on_hand <= 0);
              const inDeal = eligibleProductIds.has(p.id) || eligibleCategories.has(p.category);
              const handleClick = () => {
                if (noPrice) return toast.error("Selling price not confirmed — not available for checkout");
                if (out) return toast.error("Out of stock");
                addToCart(p);
              };
              return (
                <button
                  key={p.id}
                  data-testid={`pos-card-${p.sku}`}
                  onClick={handleClick}
                  aria-disabled={out}
                  className={`relative text-left p-3 rounded-lg border transition-colors duration-150 bg-white shadow-sm ${
                    out ? "opacity-60 cursor-not-allowed" : "hover:border-primary/40 hover:shadow-md"
                  } ${inDeal ? "ring-1 ring-primary/30" : ""}`}
                >
                  {inDeal && (
                    <div className="absolute top-1.5 right-1.5">
                      <Tag className="w-3 h-3 text-primary" strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="text-[10px] font-mono text-muted-foreground">{p.sku}</div>
                  <div className="mt-1 text-sm font-medium line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                  <div className="mt-2 flex items-end justify-between">
                    <div className="font-mono text-sm text-primary font-semibold">{currency(p.unit_price)}</div>
                    <div className={`text-[10px] uppercase tracking-widest tabular-nums ${
                      pending && !noPrice ? "text-amber-700 font-semibold" : out ? "text-[#C05746] font-semibold" : "text-muted-foreground"
                    }`}>
                      {noPrice ? "Price pending" : pending ? "Count pending" : out ? "Out of stock" : `${fmtQty(p.quantity_on_hand)} ${p.unit}`}
                    </div>
                  </div>
                  {p.special_deal && <div className="mt-1 text-[10px] text-secondary font-medium">{p.special_deal}</div>}
                </button>
              );
            })}
          </div>
          {products.length === 0 && (
            <div className="text-center text-muted-foreground py-16">No products match your filters.</div>
          )}
        </div>
      </div>

      <div className="hidden lg:flex flex-col bg-white h-full min-h-0">
        <CartPanel inSheet={false} {...cartProps} />
      </div>

      <div className="lg:hidden fixed bottom-4 inset-x-4 z-20">
        <Button
          data-testid="mobile-open-cart"
          onClick={(e) => { e.stopPropagation(); setCartOpen(true); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-base shadow-lg flex items-center justify-between px-6"
        >
          <span className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
            <span>Cart</span>
            {totalItems > 0 && <Badge className="bg-white text-primary ml-1 h-6 px-2">{totalItems}</Badge>}
          </span>
          <span className="font-mono tabular-nums" data-testid="mobile-cart-total">{currency(displayFinalTotal)}</span>
        </Button>
      </div>

      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetTitle className="sr-only">Current Sale</SheetTitle>
          <CartPanel inSheet={true} {...cartProps} />
        </SheetContent>
      </Sheet>

      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Receipt</DialogTitle>
          </DialogHeader>
          {receipt && (
            <div className="font-mono text-xs" data-testid="receipt-content">
              <div className="text-center mb-3">
                <div className="font-display text-lg not-italic">Saint Herb · KhulaTill</div>
                <div className="text-muted-foreground">{new Date(receipt.at).toLocaleString("en-ZA")}</div>
                <div className="text-muted-foreground">Receipt {receipt.receipt_no}</div>
              </div>
              <div className="border-t border-dashed py-2 space-y-1">
                {receipt.lines.map((l, i) => (
                  <div key={i} className="flex justify-between">
                    <div className="truncate">{l.quantity} × {l.name}</div>
                    <div>{currency(l.line_total)}</div>
                  </div>
                ))}
              </div>
              {receipt.deal_id && (
                <div className="border-t border-dashed pt-2 space-y-1 text-secondary">
                  <div className="flex justify-between">
                    <span>Deal: {receipt.deal_name}</span>
                    <span>− {currency(receipt.discount_amount)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Original</span>
                    <span>{currency(receipt.original_total)}</span>
                  </div>
                </div>
              )}
              {receipt.manual_discount_type && (
                <div className="border-t border-dashed pt-2 space-y-1 text-secondary" data-testid="receipt-manual-discount">
                  <div className="flex justify-between">
                    <span>
                      Manual discount
                      {receipt.manual_discount_type === "percent"
                        ? ` (${receipt.manual_discount_value}%)`
                        : ` (R${Number(receipt.manual_discount_value).toFixed(2)})`}
                    </span>
                    <span>− {currency(receipt.manual_discount_amount)}</span>
                  </div>
                </div>
              )}
              <div className="border-t border-dashed pt-2 flex justify-between font-semibold text-sm">
                <span>Total ({receipt.payment_method})</span>
                <span>{currency(receipt.total)}</span>
              </div>
              {receipt.payment_method === "Split" && receipt.payment_breakdown && (
                <div className="pt-1 text-[11px]" data-testid="receipt-split-breakdown">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Cash</span><span>{currency(receipt.payment_breakdown.cash)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Card</span><span>{currency(receipt.payment_breakdown.card)}</span>
                  </div>
                </div>
              )}
              {receipt.sale_note && (
                <div className="border-t border-dashed mt-2 pt-2 text-[11px] text-muted-foreground whitespace-pre-wrap" data-testid="receipt-sale-note">
                  <span className="uppercase tracking-widest mr-1">Note:</span>{receipt.sale_note}
                </div>
              )}
              <div className="text-center mt-3 text-muted-foreground">Cashier: {receipt.cashier_name}</div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setReceipt(null)} className="rounded-full bg-primary hover:bg-primary/90">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RegisterCustomerDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        prefillPhone={prefillPhone}
        onCreated={onCustomerRegistered}
      />

      {/* Manual Discount modal — Phase D */}
      <Dialog open={discountOpen} onOpenChange={setDiscountOpen}>
        <DialogContent className="sm:max-w-md" data-testid="discount-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {canApproveDirect ? "Add manual discount" : "Apply / request discount"}
            </DialogTitle>
            <DialogDescription>
              {canApproveDirect
                ? "Discount applies after Specials & Deals, before payment."
                : `You may apply a discount of up to ${CASHIER_PERCENT_THRESHOLD}% of the sale directly (whether entered as % or R). Larger discounts require Manager / Owner approval.`}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const available = dealPreview?.final_total ?? subtotal;
            const vRaw = parseFloat(dcValue);
            const v = isNaN(vRaw) ? 0 : vRaw;
            const clamped = v <= 0
              ? 0
              : dcType === "percent"
                ? Math.min(available, Math.round(available * (v / 100) * 100) / 100)
                : Math.min(available, Math.round(v * 100) / 100);
            const effPct = available > 0 && v > 0
              ? Math.round((clamped / available) * 10000) / 100
              : 0;
            const overThreshold = !canApproveDirect && v > 0 && effPct > CASHIER_PERCENT_THRESHOLD;
            const buttonLabel = (canApproveDirect || (v > 0 && effPct <= CASHIER_PERCENT_THRESHOLD))
              ? "Apply discount"
              : "Request approval";
            return (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Discount type</label>
                  <Select value={dcType} onValueChange={setDcType}>
                    <SelectTrigger data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Rand (R)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Value {dcType === "percent" ? "(%)" : "(R)"}
                  </label>
                  <Input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={dcValue} onChange={(e) => setDcValue(e.target.value)}
                    data-testid="discount-value-input"
                    placeholder={dcType === "percent" ? "e.g. 10" : "e.g. 50.00"}
                  />
                  {v > 0 && available > 0 && (
                    <div
                      className={`text-[11px] mt-1 ${overThreshold ? "text-[#C05746]" : "text-muted-foreground"}`}
                      data-testid="discount-effective-pct"
                    >
                      {currency(clamped)} discount = {effPct.toFixed(2)}% of this sale
                      {overThreshold && ` — above ${CASHIER_PERCENT_THRESHOLD}%, requires approval`}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Reason <span className="text-[#C05746]">*</span>
                  </label>
                  <Textarea
                    value={dcReason} onChange={(e) => setDcReason(e.target.value)}
                    data-testid="discount-reason-input"
                    placeholder="e.g. Damaged packaging"
                    rows={2} maxLength={300}
                  />
                </div>
                <DialogFooter className="pt-2">
                  <Button variant="ghost" onClick={() => setDiscountOpen(false)}>Cancel</Button>
                  <Button
                    className="rounded-full bg-primary hover:bg-primary/90"
                    onClick={submitDiscount}
                    data-testid="submit-discount-btn"
                  >
                    {buttonLabel}
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
